/**
 * Model-driven question suggestions. Instead of hard-coding sample questions, we
 * derive them from the active semantic model's own measures, dimensions, dates,
 * and enums — so the "Ask" chips always match whatever client model is loaded AND
 * showcase the full query surface (trends, top-N, averages/ratios, distinct counts,
 * multi-series, computed metrics, filters), not just plain counts. Each category is
 * its own bucket and the final set is round-robined + shuffled, so a refresh yields
 * a fresh, varied mix. Phrasing uses business names and stays plannable — the Azure
 * planner maps everyday words to fields via the glossary and self-repairs near-misses.
 */
interface Field {
  technical_name: string;
  business_name?: string;
  data_type?: string;
  is_measure_candidate?: boolean;
  is_dimension_candidate?: boolean;
  date_role?: string | null;
}
interface Entity {
  technical_name: string;
  business_name?: string;
  primary_identifier?: string;
  display_field?: string;
  fields?: Field[];
}
interface Relationship {
  parent: string;
  child: string;
  join_fields: string[];
}
interface StatusEnum {
  field: string;
  technical_value: string;
}
interface Model {
  entities?: Entity[];
  relationships?: Relationship[];
  status_enums?: StatusEnum[];
}

/** FK/identifier fields are flagged as measures by inference but shouldn't be summed. */
const ID_LIKE = /(^id$|_?id$)/i;
/** Scalar types eligible as group-by dimensions (excludes arrays/objects like `items`). */
const SCALAR = new Set(['string', 'boolean', 'integer', 'number']);
/** Name hints for the two operands of a per-row revenue expression (quantity × price). */
const PRICE_LIKE = /(price|cost|amount|rate|fee|value)/i;
const QTY_LIKE = /(qty|quantity|units?|volume|weight|count)/i;

function shuffle<T>(arr: T[]): T[] {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
}

/** technical_name → Title Case words: `paymentMethod` / `sale_items` → "Payment Method". */
function humanize(name: string): string {
  return name
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Naive pluralizer: Category → Categories, Sale → Sales, Product → Products. */
function pluralize(word: string): string {
  if (/[sxz]$|[cs]h$/i.test(word)) return `${word}es`;
  if (/[^aeiou]y$/i.test(word)) return `${word.slice(0, -1)}ies`;
  return `${word}s`;
}

const measureLabel = (f: Field): string => f.business_name || humanize(f.technical_name);
const entityWord = (e: Entity): string => e.business_name || humanize(e.technical_name);
/** A dimension's readable word, stripping a trailing FK marker: `categoryId` → "category". */
const dimWord = (f: Field): string => humanize(f.technical_name.replace(/(_id|Id)$/, '') || f.technical_name);

export function suggestQuestions(model: unknown, queryable: string[] = [], count = 6): string[] {
  // When we know which entities have a live endpoint, only suggest those — so a
  // schema-only entity (e.g. Customer, with no /api/customers) never appears.
  const canQuery = (name: string) => queryable.length === 0 || queryable.includes(name);
  const m = model as Model;
  const entities = (m?.entities ?? []).filter((e) => canQuery(e.technical_name));
  const rels = m?.relationships ?? [];
  const enums = m?.status_enums ?? [];
  const byName = new Map((m?.entities ?? []).map((e) => [e.technical_name, e]));

  // The parent entity a FK dimension points at (for label joins), or null for a flat dim.
  const fkParent = (child: string, dim: Field): Entity | null => {
    const rel = rels.find((r) => r.child === child && r.join_fields.includes(dim.technical_name));
    return rel ? byName.get(rel.parent) ?? null : null;
  };
  // A FK dim is usable only when its parent (whose label it joins to) is itself queryable.
  const dimUsable = (child: string, dim: Field): boolean => {
    const parent = fkParent(child, dim);
    if (!parent) return true; // flat dim — no join needed
    const joins = parent.display_field && parent.display_field !== parent.primary_identifier;
    return !joins || canQuery(parent.technical_name);
  };
  const dimLabel = (child: string, dim: Field): string => {
    const parent = fkParent(child, dim);
    return parent ? entityWord(parent) : dimWord(dim);
  };
  // A transactional parent (one with a Transaction date) is high-cardinality — a
  // poor breakdown dimension. Grouping line items "by sale" or orders "by customer"
  // is mechanical noise, so a FK pointing at such an entity is not a usable dim.
  const isFactEntity = (e: Entity): boolean => (e.fields ?? []).some((f) => f.date_role === 'Transaction');
  const goodDim = (child: string, dim: Field): boolean => {
    const parent = fkParent(child, dim);
    return !parent || !isFactEntity(parent);
  };

  // One bucket per QUESTION KIND, so the final chips are a mix of shapes, not six counts.
  const trend = new Set<string>(); // time series (line)
  const rank = new Set<string>(); // top-N + measure-by-dim (bar)
  const ratio = new Set<string>(); // averages / per-record ratios
  const advanced = new Set<string>(); // distinct, filters, computed, multi-measure
  const breakdown = new Set<string>(); // pie / table variety
  const basic = new Set<string>(); // totals + counts (KPI)

  // Fact entities first: the more real measures an entity has, the more it leads.
  const ranked = entities
    .map((e) => {
      const fields = e.fields ?? [];
      const measures = fields.filter((f) => f.is_measure_candidate && !ID_LIKE.test(f.technical_name));
      const dims = fields.filter(
        (f) =>
          f.is_dimension_candidate &&
          !f.date_role &&
          SCALAR.has(f.data_type ?? '') &&
          dimUsable(e.technical_name, f) &&
          goodDim(e.technical_name, f),
      );
      const date = fields.find((f) => f.date_role) ?? fields.find((f) => f.data_type === 'date' || f.data_type === 'datetime');
      return { e, measures, dims, date };
    })
    .sort((a, b) => b.measures.length - a.measures.length);

  for (const { e, measures, dims, date } of ranked) {
    const name = e.technical_name;
    const fkDims = dims.filter((d) => fkParent(name, d));
    const enumVal = enums.find((s) => s.field.startsWith(`${name}.`))?.technical_value;
    const m0 = measures[0];

    // ── Trends over time ──
    if (date) {
      if (m0) trend.add(`${measureLabel(m0)} per month`);
      trend.add(`${pluralize(entityWord(e))} over time`);
      // Multi-series: a trend split by one category (time + one dimension → many lines).
      const cat = fkDims[0] ?? dims[0];
      if (m0 && cat) trend.add(`${measureLabel(m0)} by month per ${dimLabel(name, cat)}`);
    }

    // ── Ranking: top-N and measure-by-dimension ──
    if (m0) {
      for (const d of fkDims.slice(0, 1)) rank.add(`Top 5 ${pluralize(dimLabel(name, d))} by ${measureLabel(m0)}`);
      for (const d of dims.slice(0, 2)) rank.add(`${measureLabel(m0)} by ${dimLabel(name, d)}`);
    } else {
      // No numeric measure — a lookup/status/junction table. Counting its rows
      // grouped by a category is still a real question, and every measure-by-dim
      // bucket below is gated on `m0`, so without this such an entity could only
      // ever offer "How many X" and none of its dimensions were reachable.
      for (const d of dims.slice(0, 2)) rank.add(`How many ${pluralize(entityWord(e))} by ${dimLabel(name, d)}`);
    }

    // ── Averages / ratios ──
    if (m0) {
      ratio.add(`Average ${measureLabel(m0)} per ${entityWord(e)}`);
      const d = dims[0];
      if (d) ratio.add(`Average ${measureLabel(m0)} by ${dimLabel(name, d)}`);
    }

    // ── Advanced: distinct, filters, computed, multiple measures ──
    for (const d of fkDims.slice(0, 1)) advanced.add(`How many distinct ${pluralize(dimLabel(name, d))}`);
    if (m0 && enumVal) advanced.add(`${measureLabel(m0)} for ${enumVal} ${pluralize(entityWord(e))}`);
    const priceF = measures.find((f) => PRICE_LIKE.test(f.technical_name));
    const qtyF = measures.find((f) => QTY_LIKE.test(f.technical_name) && f !== priceF);
    if (priceF && qtyF) advanced.add(`Total ${measureLabel(qtyF)} × ${measureLabel(priceF)}`);
    if (measures.length >= 2) {
      const suffix = date ? 'by month' : dims[0] ? `by ${dimLabel(name, dims[0])}` : '';
      advanced.add(`${measureLabel(measures[0]!)} and ${measureLabel(measures[1]!)} ${suffix}`.trim());
    }

    // ── Breakdown variety (pie / table) ──
    if (dims[0]) {
      const subject = m0 ? measureLabel(m0) : `How many ${pluralize(entityWord(e))}`;
      breakdown.add(`${subject} by ${dimLabel(name, dims[0])} as a table`);
      breakdown.add(`${subject} by ${dimLabel(name, dims[0])} pie chart`);
    }

    // ── Basics ──
    if (m0) basic.add(`Total ${measureLabel(m0)}`);
    basic.add(`How many ${pluralize(entityWord(e))}`);
  }

  // Round-robin across kinds (each shuffled) so the chips are a varied, advanced mix.
  const buckets = shuffle([trend, rank, ratio, advanced, breakdown, basic].map((s) => shuffle([...s])));
  const out: string[] = [];
  let progressed = true;
  while (out.length < count && progressed) {
    progressed = false;
    for (const b of buckets) {
      if (b.length && out.length < count) {
        out.push(b.shift()!);
        progressed = true;
      }
    }
  }
  return out.length ? out : ['How many records'];
}

/**
 * Session cache for the LLM Ask suggestions, so navigating away from the Ask tab
 * (which unmounts Chat) and back reuses them instead of re-hitting the model —
 * only an explicit Refresh replaces them. sessionStorage so a reload within the
 * tab keeps them; it dies with the tab. SSR-safe (no window on the server render).
 */
const SUGGESTIONS_CACHE_KEY = 'dgt_ask_suggestions';

export function getCachedSuggestions(): string[] | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(SUGGESTIONS_CACHE_KEY);
    return raw ? (JSON.parse(raw) as string[]) : null;
  } catch {
    return null;
  }
}

export function setCachedSuggestions(questions: string[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(SUGGESTIONS_CACHE_KEY, JSON.stringify(questions));
  } catch {
    /* storage full/blocked — the in-memory state still serves this view */
  }
}
